import React, { ReactNode } from 'react';
import {
  Box,
  Text,
  Code,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Stack,
  SimpleGrid,
  Divider,
  HStack,
  Button,
  Icon,
} from '@chakra-ui/react';
import { GitCompare } from 'lucide-react';
import type { BillableDetails, BillableJsonResponse } from './types';

const DetailKV: React.FC<{ label: string; value?: ReactNode }> = React.memo(({ label, value }) => {
  if (value == null || value === '') {
    return null;
  }
  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" noOfLines={3}>
        {value}
      </Text>
    </Box>
  );
});
DetailKV.displayName = 'DetailKV';

const JsonBlock: React.FC<{
  label: string;
  value: BillableJsonResponse | null | undefined;
}> = React.memo(({ label, value }) => {
  if (value == null) {
    return null;
  }
  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={1}>
        {label}
      </Text>
      <Code p={3} w="100%" whiteSpace="pre" display="block" overflowX="auto">
        {JSON.stringify(value, null, 2)}
      </Code>
    </Box>
  );
});
JsonBlock.displayName = 'JsonBlock';

type BillableDetailsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  details: BillableDetails | null;
  onOpenComparison: () => void;
};

export const BillableDetailsDrawer: React.FC<BillableDetailsDrawerProps> = React.memo(
  ({ isOpen, onClose, details, onOpenComparison }) => {
    return (
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <HStack justify="space-between">
              <Text>Billable Details</Text>
              <HStack spacing={2}>
                {details?.xeroInvoiceNumber ? (
                  <Code colorScheme="green">Xero #{details?.xeroInvoiceNumber}</Code>
                ) : null}
                {details?.xeroInvoiceNumber ? (
                  <Button
                    size="sm"
                    leftIcon={<Icon as={GitCompare} boxSize={4} />}
                    onClick={onOpenComparison}
                    variant="outline"
                    mr={10}
                  >
                    Compare with Xero
                  </Button>
                ) : null}
              </HStack>
            </HStack>
          </DrawerHeader>
          <DrawerBody>
            {details ? (
              <Stack spacing={6}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <DetailKV label="Billable ID" value={details.id} />
                  <DetailKV label="Provider" value={details.providerId} />
                  <DetailKV label="Status" value={details.status} />
                  <DetailKV label="Invoice Date" value={details.invoiceDate} />
                  <DetailKV label="Due Date" value={details.dueDate} />
                  <DetailKV label="Description" value={details.description} />
                  <DetailKV label="Quantity" value={details.quantity} />
                  <DetailKV label="Unit Amount" value={details.unitAmount} />
                  <DetailKV label="Currency" value={details.currency} />
                  <DetailKV label="Account Code" value={details.accountCode} />
                  <DetailKV label="Contact Name" value={details.contactName} />
                  <DetailKV label="Event Type" value={details.eventType} />
                  <DetailKV label="Event Source ID" value={details.eventSourceId} />
                  <DetailKV label="Event Occurred At" value={details.eventOccurredAt} />
                  <DetailKV label="Event Source Kind" value={details.eventSourceKind} />
                  <DetailKV label="Xero Invoice ID" value={details.xeroInvoiceId} />
                  <DetailKV label="Xero Invoice #" value={details.xeroInvoiceNumber} />
                  <DetailKV label="Xero PDF URL" value={details.xeroPdfUrl} />
                  <DetailKV label="Last Synced At" value={details.lastSyncedAt} />
                </SimpleGrid>

                <Divider />
                <JsonBlock label="Pushed Payload" value={details.pushedPayload} />
                <JsonBlock label="Authorise Payload" value={details.authorisePayload} />
                <JsonBlock label="Void Payload" value={details.voidPayload} />
              </Stack>
            ) : (
              <Text color="gray.500" fontSize="sm">
                No details available.
              </Text>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  },
);
BillableDetailsDrawer.displayName = 'BillableDetailsDrawer';
